import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useIonToast } from '@ionic/react';
import { debounce } from 'lodash';

import { PlayerService } from 'services/player';
import { PlaylistReduxCommandCreator } from 'appRedux/actions/playlistCommandCreator';
import { IUserPlaylistItem } from 'appRedux/models/playerModels';
import {
  bustPlaylistCache,
  isPlayerScopedLoaded,
  playerScopedKey,
  playlistListBucket,
  sharedPlayerScopedGet,
} from 'lib/playerScopedListCache';

interface PlaylistHookProps {
  onOperationComplete?: () => void;
}

const toPlaylistSlotNumber = (slotNumber: string | number): number => {
  const n = typeof slotNumber === 'number' ? slotNumber : Number(String(slotNumber).trim());
  return Number.isFinite(n) ? n : NaN;
};

const usePlaylist = ({ onOperationComplete }: PlaylistHookProps = {}) => {
  const playerSvc = new PlayerService();
  const [presentToast] = useIonToast();
  const dispatch = useDispatch();

  const [isPlaylistLoading, setIsPlaylistLoading] = useState(false);

  const playlistCommandCreator = PlaylistReduxCommandCreator(dispatch);

  // Once per appUserPk+casinoId (session). Remounts during timeframe/insight
  // reloads must not re-hit GetUserPlaylist — shared cache + in-flight dedupe.
  // Cache hit must NOT SetPlaylist or it would clobber optimistic add/remove.
  const fetchUserPlaylist = useCallback((userId: string, casinoId: string) => {
    if (!userId || !casinoId) return;
    const key = playerScopedKey(userId, casinoId);
    const alreadyLoaded = isPlayerScopedLoaded(playlistListBucket, key);
    if (!alreadyLoaded) setIsPlaylistLoading(true);

    const subscription = sharedPlayerScopedGet(playlistListBucket, key, () =>
      playerSvc.GetUserPlaylist(userId, casinoId)
    ).subscribe({
      next(response) {
        setIsPlaylistLoading(false);
        if (response && !alreadyLoaded) {
          playlistCommandCreator.SetPlaylist(response.DataSet);
        }
      },
      error() {
        setIsPlaylistLoading(false);
        presentToast({
          position: 'top',
          duration: 5000,
          color: 'danger',
          message: 'Failed to get Playlist.',
        }).catch(() => null);
      },
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const handleInsertPlaylist = useCallback(
    debounce(
      (
        slotNumber: string,
        userId: string,
        casinoId: string,
        showSuccessToast = false
      ) => {
        if (!userId || !casinoId) return;
        setIsPlaylistLoading(true);
        let succeeded = false;

        const id = toPlaylistSlotNumber(slotNumber);
        const subscription = playerSvc.InsertPlaylist(slotNumber, userId, casinoId).subscribe({
          next(response) {
            if (response) {
              if (Number.isFinite(id)) {
                playlistCommandCreator.AddPlaylistItem({
                  slotNumber: id,
                });
              }
              bustPlaylistCache(userId, casinoId);
              succeeded = true;
            }
          },
          error() {
            presentToast({
              position: 'top',
              duration: 5000,
              color: 'danger',
              message: 'Failed to add to Playlist.',
            }).catch(() => null);
          },
          complete() {
            setIsPlaylistLoading(false);

            if (showSuccessToast && succeeded) {
              presentToast({
                position: 'top',
                duration: 3000,
                cssClass: 'player-linked-toast',
                message: '1 slot added to Playlist.',
              }).catch(() => null);
            }

            onOperationComplete && onOperationComplete();
          },
        });

        return () => {
          subscription.unsubscribe();
        };
      },
      500
    ),
    []
  );

  const handleInsertPlaylistBulk = useCallback(
    (slotNumbers: string[], userId: string, casinoId: string) => {
      if (!userId || !casinoId || slotNumbers.length === 0) return;
      setIsPlaylistLoading(true);

      let remaining = slotNumbers.length;
      let added = 0;
      let failed = 0;

      const finish = () => {
        setIsPlaylistLoading(false);
        if (added > 0) bustPlaylistCache(userId, casinoId);
        if (added > 0) {
          presentToast({
            position: 'top',
            duration: 3000,
            cssClass: 'player-linked-toast',
            message:
              added === 1
                ? '1 slot added to Playlist.'
                : `${added} slots added to Playlist.`,
          }).catch(() => null);
        }
        if (failed > 0) {
          presentToast({
            position: 'top',
            duration: 5000,
            color: 'danger',
            message:
              failed === 1
                ? 'Failed to add 1 slot to Playlist.'
                : `Failed to add ${failed} slots to Playlist.`,
          }).catch(() => null);
        }
        onOperationComplete && onOperationComplete();
      };

      slotNumbers.forEach((slotNumber) => {
        const id = toPlaylistSlotNumber(slotNumber);
        playerSvc.InsertPlaylist(slotNumber, userId, casinoId).subscribe({
          next(response) {
            if (response) {
              if (Number.isFinite(id)) {
                playlistCommandCreator.AddPlaylistItem({
                  slotNumber: id,
                });
              }
              added += 1;
            }
          },
          error() {
            failed += 1;
            remaining -= 1;
            if (remaining === 0) finish();
          },
          complete() {
            remaining -= 1;
            if (remaining === 0) finish();
          },
        });
      });
    },
    []
  );

  const handleDeletePlaylist = useCallback(
    debounce(
      (
        slotNumber: string,
        userId: string,
        casinoId: string,
        showSuccessToast = false
      ) => {
        if (!userId || !casinoId) return;
        setIsPlaylistLoading(true);
        let succeeded = false;
        const id = toPlaylistSlotNumber(slotNumber);

        const subscription = playerSvc.DeletePlaylist(slotNumber, userId, casinoId).subscribe({
          next(response) {
            if (response) {
              if (Number.isFinite(id)) playlistCommandCreator.RemovePlaylistItem(id);
              bustPlaylistCache(userId, casinoId);
              succeeded = true;
            }
          },
          error() {
            presentToast({
              position: 'top',
              duration: 5000,
              color: 'danger',
              message: 'Failed to remove from Playlist.',
            }).catch(() => null);
          },
          complete() {
            setIsPlaylistLoading(false);

            if (showSuccessToast && succeeded) {
              presentToast({
                position: 'top',
                duration: 3000,
                cssClass: 'player-linked-toast',
                message: '1 slot removed from Playlist.',
              }).catch(() => null);
            }

            onOperationComplete && onOperationComplete();
          },
        });

        return () => {
          subscription.unsubscribe();
        };
      },
      500
    ),
    []
  );

  const handleDeletePlaylistBulk = useCallback(
    (slotNumbers: string[], userId: string, casinoId: string) => {
      if (!userId || !casinoId || slotNumbers.length === 0) return;
      setIsPlaylistLoading(true);

      let remaining = slotNumbers.length;
      let removed = 0;
      let failed = 0;
      // Collect the slots that were removed server-side and update Redux in a
      // single dispatch on completion, so a dependent list refresh fires once.
      const removedSlots: number[] = [];

      const finish = () => {
        setIsPlaylistLoading(false);
        if (removedSlots.length > 0) {
          playlistCommandCreator.RemovePlaylistItems(removedSlots);
          bustPlaylistCache(userId, casinoId);
        }
        if (removed > 0) {
          presentToast({
            position: 'top',
            duration: 3000,
            cssClass: 'player-linked-toast',
            message:
              removed === 1
                ? '1 slot removed from Playlist.'
                : `${removed} slots removed from Playlist.`,
          }).catch(() => null);
        }
        if (failed > 0) {
          presentToast({
            position: 'top',
            duration: 5000,
            color: 'danger',
            message:
              failed === 1
                ? 'Failed to remove 1 slot from Playlist.'
                : `Failed to remove ${failed} slots from Playlist.`,
          }).catch(() => null);
        }
        onOperationComplete && onOperationComplete();
      };

      slotNumbers.forEach((slotNumber) => {
        const id = toPlaylistSlotNumber(slotNumber);
        playerSvc.DeletePlaylist(slotNumber, userId, casinoId).subscribe({
          next(response) {
            if (response) {
              if (Number.isFinite(id)) removedSlots.push(id);
              removed += 1;
            }
          },
          error() {
            failed += 1;
            remaining -= 1;
            if (remaining === 0) finish();
          },
          complete() {
            remaining -= 1;
            if (remaining === 0) finish();
          },
        });
      });
    },
    []
  );

  return {
    isPlaylistLoading,
    fetchUserPlaylist,
    handleInsertPlaylist,
    handleInsertPlaylistBulk,
    handleDeletePlaylist,
    handleDeletePlaylistBulk,
  };
};

export const isIncludedInPlaylist = (
  slotNumber: string | number,
  playlist: IUserPlaylistItem[] = []
): boolean => {
  const needle = String(slotNumber ?? '').trim();
  if (!needle) return false;
  const needleNum = Number(needle);
  return playlist.some((item) => {
    const stored = String(item.slotNumber ?? '').trim();
    if (!stored) return false;
    return stored === needle || (Number.isFinite(needleNum) && Number(stored) === needleNum);
  });
};

export default usePlaylist;
